import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, CheckCircle, AlertCircle, Loader2, Building2, DollarSign, Users, ArrowRight } from "lucide-react";
import { LuxuryBackground, Card, GoldButton, PageHero, inputClassLight as inputClass, selectClassLight as selectClass, labelClassLight as labelClass } from "../components/shared/UIComponents";
import { API } from "../data/constants";
import { usePageNavigate } from "../hooks/usePageNavigate";

export default function ContactPage() {
  const { navigateTo } = usePageNavigate();
  const [contactForm, setContactForm] = useState({ fullName: "", email: "", phone: "", inquiryType: "", message: "" });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState(null);
  const handleContactChange = (k, v) => setContactForm(p => ({ ...p, [k]: v }));

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactError(null);
    try {
      await axios.post(`${API}/contact`, { full_name: contactForm.fullName, email: contactForm.email, phone: contactForm.phone || null, accreditation_status: contactForm.inquiryType || null, interest_summary: contactForm.message || null });
      setContactSubmitted(true);
    } catch (err) {
      setContactError(err.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="relative" data-testid="contact-page">
      <PageHero eyebrow="Contact" title="Get in Touch"
        description="Whether you have a property to sell, capital to deploy, or a question about how we operate — our team is ready to connect." />
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-20 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            <Card className="md:col-span-1">
              <div className="w-fit rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 p-3 border border-emerald-200"><Mail className="h-6 w-6 text-emerald-700" /></div>
              <div className="mt-5 text-xs uppercase tracking-[0.3em] text-[#888]">Email</div>
              <a href="mailto:connect@imaanicapital.com" className="mt-3 block break-all text-base sm:text-xl font-bold tracking-tight text-[#0A0A0A] hover:text-emerald-700 transition-colors" data-testid="email-link">connect@imaanicapital.com</a>
              <div className="mt-6 text-xs uppercase tracking-[0.3em] text-[#888]">Headquarters</div>
              <p className="mt-2 text-sm font-semibold text-[#0A0A0A]">Houston, Texas</p>
            </Card>
            <Card className="md:col-span-2">
              <h3 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">How Can We Help?</h3>
              <div className="mt-6 grid gap-3 md:grid-cols-3">
                <motion.button onClick={() => navigateTo("sell")} whileHover={{ scale: 1.02 }}
                  className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-transparent p-4 text-left transition hover:border-emerald-400 hover:shadow-sm" data-testid="contact-seller-cta">
                  <Building2 className="h-5 w-5 text-emerald-600 mb-2" />
                  <div className="text-base font-bold text-[#0A0A0A]">Sell a Property</div>
                  <div className="mt-1 text-xs text-emerald-700 font-medium">Submit for a no-obligation offer</div>
                </motion.button>
                <motion.button onClick={() => navigateTo("investors")} whileHover={{ scale: 1.02 }}
                  className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-transparent p-4 text-left transition hover:border-emerald-400 hover:shadow-sm" data-testid="contact-investor-cta">
                  <DollarSign className="h-5 w-5 text-emerald-600 mb-2" />
                  <div className="text-base font-bold text-[#0A0A0A]">Capital Partnership</div>
                  <div className="mt-1 text-xs text-emerald-700 font-medium">Co-invest or join our fund</div>
                </motion.button>
                <motion.button onClick={() => navigateTo("acquisitions")} whileHover={{ scale: 1.02 }}
                  className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-transparent p-4 text-left transition hover:border-emerald-400 hover:shadow-sm" data-testid="contact-broker-cta">
                  <Users className="h-5 w-5 text-emerald-600 mb-2" />
                  <div className="text-base font-bold text-[#0A0A0A]">Broker Inquiry</div>
                  <div className="mt-1 text-xs text-emerald-700 font-medium">Bring us your off-market deals</div>
                </motion.button>
              </div>
            </Card>
          </div>
          <Card hover={false} className="mt-8">
            <h3 className="text-2xl font-bold tracking-tight text-[#0A0A0A] mb-6">Send Us a Message</h3>
            {contactSubmitted ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
                <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-emerald-600 mb-2">Message Sent!</h4>
                <p className="text-[#333]">Thank you for reaching out. Our team will respond within 1 business day.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleContactSubmit} className="grid gap-5 md:grid-cols-2">
                <label className="block"><span className={labelClass}>Full Name *</span>
                  <input required value={contactForm.fullName} onChange={e => handleContactChange("fullName", e.target.value)} className={inputClass} placeholder="Full Name" data-testid="contact-fullName" /></label>
                <label className="block"><span className={labelClass}>Email *</span>
                  <input required type="email" value={contactForm.email} onChange={e => handleContactChange("email", e.target.value)} className={inputClass} placeholder="Email" data-testid="contact-email" /></label>
                <label className="block"><span className={labelClass}>Phone</span>
                  <input value={contactForm.phone} onChange={e => handleContactChange("phone", e.target.value)} className={inputClass} placeholder="Phone" data-testid="contact-phone" /></label>
                <label className="block"><span className={labelClass}>Inquiry Type</span>
                  <select value={contactForm.inquiryType} onChange={e => handleContactChange("inquiryType", e.target.value)} className={selectClass} data-testid="contact-inquiryType">
                    <option value="">Select an option</option>
                    <option value="seller">I have a property to sell</option>
                    <option value="investor">Capital partnership / investment</option>
                    <option value="broker">Broker with off-market deal</option>
                    <option value="other">General inquiry</option>
                  </select></label>
                <label className="block md:col-span-2"><span className={labelClass}>Message</span>
                  <textarea value={contactForm.message} onChange={e => handleContactChange("message", e.target.value)} rows={4}
                    className={inputClass + " resize-none"} placeholder="Tell us about your property, investment goals, or how we can help..." data-testid="contact-message" /></label>
                {contactError && <div className="md:col-span-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600 flex items-center gap-2"><AlertCircle className="h-5 w-5" />{contactError}</div>}
                <div className="md:col-span-2">
                  <GoldButton type="submit" disabled={contactLoading} data-testid="contact-submit-btn">
                    {contactLoading ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Sending...</span> : "Send Message"}
                  </GoldButton>
                  <p className="mt-3 text-xs text-[#999]" data-testid="response-time-note">Our team typically responds within 1 business day.</p>
                </div>
              </form>
            )}
          </Card>
        </section>
      </div>
    </div>
  );
}
